import React, { useState, useEffect, useRef, useCallback } from 'react';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useChat } from '../../contexts/ChatContext';
import chatService from '../../services/chatService';
import ChatNotification from './ChatNotification';
import './chat.css';

const ChatNotificationContainer = () => {
  const { currentUser } = useAuth();
  const { markAsRead } = useChat();
  const [notifications, setNotifications] = useState([]);
  const navigate = useNavigate();
  const timeoutRefs = useRef({});

  // Handle new incoming messages
  useEffect(() => {
    const handleNewMessage = (message) => {
      // Don't show notification if it's from the current user or if there's no content
      if (message.senderId === currentUser?.id || !message.content) return;

      const notificationId = `notif-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      const newNotification = {
        id: notificationId,
        message: {
          ...message,
          content: message.content.length > 100 
            ? `${message.content.substring(0, 100)}...` 
            : message.content,
        },
        sender: message.sender || { name: 'Utilisateur' },
        conversationId: message.conversationId || message.conversation?.id,
        createdAt: message.createdAt || new Date().toISOString(),
      };

      setNotifications(prev => [newNotification, ...prev]);

      // Clear any existing timeout for this conversation
      if (timeoutRefs.current[message.conversationId]) {
        clearTimeout(timeoutRefs.current[message.conversationId]);
      }

      // Auto-remove notification after delay
      timeoutRefs.current[message.conversationId] = setTimeout(() => {
        removeNotification(notificationId);
      }, 10000); // 10 seconds
    };

    // Subscribe to new messages using the correct method
    const unsubscribeMessage = chatService.on('message', handleNewMessage);
    
    return () => {
      // Cleanup function
      if (typeof unsubscribeMessage === 'function') {
        unsubscribeMessage();
      }
      // Clear all timeouts on unmount
      Object.values(timeoutRefs.current).forEach(clearTimeout);
    };
  }, [currentUser?.id]);

  const removeNotification = (id) => {
    setNotifications(prev => {
      const notification = prev.find(n => n.id === id);
      if (notification) {
        // Clear the timeout when manually removing
        if (timeoutRefs.current[notification.conversationId]) {
          clearTimeout(timeoutRefs.current[notification.conversationId]);
          delete timeoutRefs.current[notification.conversationId];
        }
      }
      return prev.filter(notif => notif.id !== id);
    });
  };

  const handleNotificationClick = (conversationId) => {
    // Mark messages as read when clicking notification
    if (conversationId) {
      markAsRead(conversationId);
      navigate(`/messages/${conversationId}`);
    } else {
      navigate('/messages');
    }
    
    // Remove all notifications for this conversation
    setNotifications(prev => {
      const remaining = prev.filter(notif => notif.conversationId !== conversationId);
      return remaining;
    });
  };

  // Créer une référence pour chaque notification
  const notificationsWithRefs = notifications.map(notif => ({
    ...notif,
    nodeRef: React.createRef()
  }));

  return (
    <div className="chat-notification-container">
      <TransitionGroup component={null}>
        {notificationsWithRefs.map(notif => (
          <CSSTransition
            key={notif.id}
            nodeRef={notif.nodeRef}
            timeout={300}
            classNames="chat-notification"
          >
            <div ref={notif.nodeRef}>
              <ChatNotification
                notification={notif}
                onClose={removeNotification}
                onClick={handleNotificationClick}
              />
            </div>
          </CSSTransition>
        ))}
      </TransitionGroup>
    </div>
  );
};

export default ChatNotificationContainer;
